import { PageHeader } from "@bednbody/ui/components/page-header";
import { SurfaceCard } from "@bednbody/ui/components/surface-card";
// Restrictions — staff restriction and ban (0309; Luigi BL-03, Leon 2026-10-09).
//
// Hub coordinator: requests (own hub). Admin / Superadmin: approve, reject,
// lift an Indefinite one. Superadmin: the Standard duration. Upcoming bookings
// of restricted staff are listed here for a person to handle — nothing is
// reassigned automatically. The function and SQL re-check every role.

import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@bednbody/ui/components/field";
import { Input } from "@bednbody/ui/components/input";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { Textarea } from "@bednbody/ui/components/textarea";
import { requestRestrictionSchema } from "@bednbody/types";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { EdgeFunctionError } from "@/lib/edge-functions";
import {
  type FlaggedBooking,
  KIND_LABEL,
  type RestrictionKind,
  type RestrictionRow,
  useDecideRestriction,
  useFlaggedBookings,
  useLiftRestriction,
  useRequestRestriction,
  useRestrictableStaff,
  useRestrictions,
  useSetDuration,
  useStandardDuration,
} from "./restrictions.hooks";

const when = (iso: string) =>
  new Date(iso).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });
const day = (iso: string) => new Date(iso).toLocaleDateString("en-PH", { dateStyle: "medium" });

const messageOf = (e: unknown) =>
  e instanceof EdgeFunctionError ? e.message : "Something went wrong. Try again.";

type StatusView = "requested" | "in_force" | "ended" | "rejected" | "lifted";
const STATUS_VIEW_LABEL: Record<StatusView, string> = {
  requested: "Waiting for approval",
  in_force: "In force",
  ended: "Ended",
  rejected: "Rejected",
  lifted: "Lifted",
};
const STATUS_TONE: Record<StatusView, "warning-soft" | "destructive-soft" | "muted-soft"> = {
  requested: "warning-soft",
  in_force: "destructive-soft",
  ended: "muted-soft",
  rejected: "muted-soft",
  lifted: "muted-soft",
};

function statusView(r: RestrictionRow): StatusView {
  if (r.status === "approved") return r.inForce ? "in_force" : "ended";
  return r.status;
}

function DurationCard() {
  const duration = useStandardDuration();
  const save = useSetDuration();
  const [days, setDays] = useState("");
  const [error, setError] = useState<string | null>(null);

  return (
    <SurfaceCard className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-primary-dark">Standard restriction length</h2>
        <p className="text-sm text-muted-foreground">
          {duration.data
            ? `Currently ${duration.data} days, for every hub. Applies to restrictions approved from now on.`
            : "Not set. A Standard restriction can't be approved until you set it."}
        </p>
      </div>
      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const n = Number(days);
          if (!Number.isInteger(n) || n < 1 || n > 3650) {
            setError("Enter a whole number of days, 1 to 3650.");
            return;
          }
          setError(null);
          save.mutate({ days: n }, { onError: (err) => setError(messageOf(err)) });
        }}
      >
        <Field className="max-w-40">
          <FieldLabel htmlFor="restriction-days">Days</FieldLabel>
          <Input
            id="restriction-days"
            type="number"
            min={1}
            max={3650}
            step={1}
            value={days}
            onChange={(e) => setDays(e.target.value)}
          />
        </Field>
        <Button type="submit" disabled={save.isPending}>
          Save
        </Button>
      </form>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </SurfaceCard>
  );
}

function RequestDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const staff = useRestrictableStaff(open);
  const request = useRequestRestriction();
  const [staffId, setStaffId] = useState("");
  const [kind, setKind] = useState<RestrictionKind>("standard");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setStaffId("");
    setKind("standard");
    setReason("");
    setError(null);
    onClose();
  };

  const submit = () => {
    const parsed = requestRestrictionSchema.safeParse({ staffId, kind, reason });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Fill in every field.");
      return;
    }
    request.mutate(parsed.data, { onSuccess: close, onError: (e) => setError(messageOf(e)) });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request a restriction</DialogTitle>
          <DialogDescription>
            An HQ admin approves or rejects it. Once approved it takes effect after their current
            job.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field>
            <FieldLabel htmlFor="restrict-staff">Staff member</FieldLabel>
            <NativeSelect
              id="restrict-staff"
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
            >
              <NativeSelectOption value="">Choose…</NativeSelectOption>
              {(staff.data ?? []).map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.full_name} ({s.role})
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor="restrict-kind">Type</FieldLabel>
            <NativeSelect
              id="restrict-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as RestrictionKind)}
            >
              {(Object.keys(KIND_LABEL) as RestrictionKind[]).map((k) => (
                <NativeSelectOption key={k} value={k}>
                  {KIND_LABEL[k]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <FieldDescription>
              {kind === "banned"
                ? "Permanent. A ban can never be lifted."
                : kind === "indefinite"
                  ? "Until an HQ admin lifts it."
                  : "For the standard length set by the Superadmin."}
            </FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor="restrict-reason">Reason</FieldLabel>
            <Textarea
              id="restrict-reason"
              rows={3}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button disabled={request.isPending} onClick={submit}>
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RestrictionsPage() {
  const { claims } = useAuth();
  const role = claims?.role;
  const canDecide = role === "admin" || role === "superadmin";
  const isSuper = role === "superadmin";

  const restrictions = useRestrictions();
  const flagged = useFlaggedBookings();
  const decide = useDecideRestriction();
  const lift = useLiftRestriction();
  const [requesting, setRequesting] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: "error" | "ok"; text: string } | null>(null);

  const runDecide = (r: RestrictionRow, approve: boolean) => {
    setNotice(null);
    decide.mutate(
      { restrictionId: r.id, approve },
      {
        onSuccess: (result) => {
          if (!approve) {
            setNotice({ tone: "ok", text: `Rejected the request for ${r.staffName}.` });
            return;
          }
          const trade = (
            result as { trade?: { traded?: unknown[]; not_traded?: { date: string }[] } }
          ).trade;
          const traded = trade?.traded?.length ?? 0;
          const kept = trade?.not_traded ?? [];
          setNotice({
            tone: "ok",
            text:
              `Approved for ${r.staffName}. ${traded} secured session${traded === 1 ? "" : "s"} sent to Trade.` +
              (kept.length > 0
                ? ` ${kept.length} couldn't go to Trade yet (${kept.map((k) => k.date).join(", ")}) — they go when the week is finalized, or handle them by hand.`
                : ""),
          });
        },
        onError: (e) => setNotice({ tone: "error", text: messageOf(e) }),
      },
    );
  };

  const runLift = (r: RestrictionRow) => {
    setNotice(null);
    lift.mutate(
      { restrictionId: r.id },
      {
        onSuccess: () =>
          setNotice({ tone: "ok", text: `Lifted the restriction on ${r.staffName}.` }),
        onError: (e) => setNotice({ tone: "error", text: messageOf(e) }),
      },
    );
  };

  const columns: ColumnDef<RestrictionRow, unknown>[] = [
    {
      id: "staff",
      accessorFn: (r) => r.staffName,
      header: "Staff",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.staffName}
          subtitle={row.original.staffRole}
          fallback={row.original.staffName.slice(0, 2).toUpperCase()}
        />
      ),
    },
    { id: "kind", accessorFn: (r) => KIND_LABEL[r.kind], header: "Type" },
    {
      id: "status",
      accessorFn: (r) => statusView(r),
      header: "Status",
      cell: ({ row }) => {
        const v = statusView(row.original);
        return <Badge variant={STATUS_TONE[v]}>{STATUS_VIEW_LABEL[v]}</Badge>;
      },
    },
    {
      id: "until",
      accessorFn: (r) => r.endsAt ?? "",
      header: "Until",
      cell: ({ row }) =>
        row.original.kind === "banned"
          ? "Permanent"
          : row.original.endsAt
            ? day(row.original.endsAt)
            : row.original.status === "approved"
              ? "Until lifted"
              : "—",
    },
    {
      id: "requested",
      accessorFn: (r) => r.requestedAt,
      header: "Requested",
      cell: ({ row }) => when(row.original.requestedAt),
    },
    {
      id: "appeal",
      accessorFn: (r) => (r.appeal ? "yes" : ""),
      header: "Appeal",
      cell: ({ row }) => (row.original.appeal ? <Badge variant="info-soft">Appealed</Badge> : null),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        const actions = [
          ...(canDecide && r.status === "requested"
            ? [
                { label: "Approve", onSelect: () => runDecide(r, true) },
                { label: "Reject", onSelect: () => runDecide(r, false) },
              ]
            : []),
          ...(canDecide && r.kind === "indefinite" && r.inForce
            ? [{ label: "Lift restriction", onSelect: () => runLift(r) }]
            : []),
        ];
        return actions.length > 0 ? (
          <AdminTableRowMenu label={r.staffName} actions={actions} />
        ) : null;
      },
    },
  ];

  const flaggedColumns: ColumnDef<FlaggedBooking, unknown>[] = [
    { id: "ref", accessorFn: (b) => b.ref ?? b.bookingId.slice(0, 8), header: "Booking" },
    {
      id: "when",
      accessorFn: (b) => b.scheduledAt,
      header: "When",
      cell: ({ row }) => when(row.original.scheduledAt),
    },
    { id: "staff", accessorFn: (b) => `${b.staffName} (${b.staffRole})`, header: "Assigned to" },
    { id: "status", accessorFn: (b) => b.status, header: "Booking status" },
  ];

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        actions={<Button onClick={() => setRequesting(true)}>Request a restriction</Button>}
        description="Restrict or ban a therapist or rider. A hub coordinator requests; an HQ admin decides."
        title="Restrictions"
      />

      {notice ? (
        <p
          role={notice.tone === "error" ? "alert" : "status"}
          className={notice.tone === "error" ? "text-sm text-destructive" : "text-sm text-success"}
        >
          {notice.text}
        </p>
      ) : null}

      {isSuper ? <DurationCard /> : null}

      {(flagged.data?.length ?? 0) > 0 ? (
        <AdminDataTable
          presentation="standalone"
          title="Upcoming bookings to handle"
          description="Assigned to someone who is restricted for that time. Nothing is reassigned automatically — reassign or cancel each one."
          data={flagged.data ?? []}
          columns={flaggedColumns}
          getRowId={(b) => `${b.bookingId}-${b.staffName}`}
          searchText={(b) => [b.ref ?? "", b.staffName].join(" ")}
          searchPlaceholder="Search booking or name…"
        />
      ) : null}

      {restrictions.error ? (
        <p className="text-sm text-destructive">{(restrictions.error as Error).message}</p>
      ) : (
        <AdminDataTable
          presentation="standalone"
          data={restrictions.data ?? []}
          columns={columns}
          loading={restrictions.isLoading}
          getRowId={(r) => r.id}
          searchText={(r) => [r.staffName, r.reason, KIND_LABEL[r.kind]].join(" ")}
          searchPlaceholder="Search name or reason…"
          facets={[
            {
              id: "status",
              label: "Status",
              options: (Object.keys(STATUS_VIEW_LABEL) as StatusView[]).map((v) => ({
                value: v,
                label: STATUS_VIEW_LABEL[v],
              })),
              getValue: (r) => statusView(r),
            },
          ]}
          onRowClick={(r) => setExpanded((cur) => (cur === r.id ? null : r.id))}
          rowLabel={(r) => `Show reason and appeal for ${r.staffName}`}
          isRowExpanded={(r) => r.id === expanded}
          renderExpandedRow={(r) => (
            <div className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Reason: </span>
                {r.reason}
              </p>
              {r.appeal ? (
                <p>
                  <span className="text-muted-foreground">
                    Appeal ({when(r.appeal.createdAt)}):{" "}
                  </span>
                  {r.appeal.body}
                </p>
              ) : null}
            </div>
          )}
          emptyTitle="No restrictions"
          emptyDescription="Requests appear here for an HQ admin to decide."
        />
      )}

      <RequestDialog open={requesting} onClose={() => setRequesting(false)} />
    </div>
  );
}
